import React, { useEffect, useState } from "react";
import instance from "../../../axios/instance";
import { getToken } from "../../../utils/token";
import { useLoaderData, useRevalidator } from "react-router-dom";
import {
  CloseCircle,
  CloseSquare,
  DocumentDownload,
  Setting2,
} from "iconsax-react";
import Spinner from "../../../components/spinners/Spinner";
import { toast } from "react-toastify";
import PrimaryAddButton from "../../../components/buttons/PrimaryAddButton";
import CardInfoModal from "../../../components/modals/CardModal";

export const subscriptionLoader = async () => {
  const token = getToken();
  const res = await instance.get("/doctor/billing/plan", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const currentAdmin = await instance.get("/doctor", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const facility = await instance.get("/doctor/facility", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return {
    plans: res.data,
    admin: currentAdmin.data,
    facility: facility.data,
  };
};

export const SubscriptionPage = () => {
  const { plans, admin, facility } = useLoaderData();
  const [loading, setLoading] = useState("");
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openFreePlanModal, setOpenFreePlanModal] = useState(false);
  const [viewingPlan, setViewingPlan] = useState(null);
  const [history, setHistory] = useState([]);
  const [openCardModal, setOpenCardModal] = useState(false);
  const { revalidate } = useRevalidator();
  const token = getToken();

  useEffect(() => {
    let mounted = true;
    (async () => {
      const history = await instance.get("/doctor/billing/history", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (mounted) {
        setHistory(history.data);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const handleTransfer = async () => {
    try {
      setLoading("transfer");
      const res = await instance.post(
        "/doctor/billing/switch-to-mx",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      window.location.reload();
      toast.success("Subscription transferred");
      setLoading("");
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occurred");
      setLoading("");
    }
  };

  const createNewSession = async (productId, withSubscription) => {
    try {
      setLoading(productId);
      const res = await instance.post(
        withSubscription
          ? "/doctor/billing/change-subscription"
          : "/doctor/billing/new-session",
        { productId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      revalidate();
      toast.success("Updated subscription");
      setLoading("");
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "An error occurred trying to bill you, please try again"
      );
      setLoading("");
    }
  };

  const handlePlanChange = async (plan, index) => {
    if (plan.currentPlan && plan.name.toLowerCase() == "free") {
      return;
    }
    if (plan.currentPlan && plan.name.toLowerCase() !== "free") {
      setOpenCancelModal(true);
      return;
    }

    if (plan.name.toLowerCase() === "free") {
      setOpenFreePlanModal(true);
      return;
    }
    const activePlan = plans.find((plan) => plan.currentPlan);
    await createNewSession(plan.id, Boolean(activePlan?.contract_id));
    return;
  };
  const statusBgs = [
    {
      key: "draft",
      bg: "#f4fdfd",
    },
    {
      key: "open",
      bg: "#93dfff",
    },
    {
      key: "settled",
      bg: "#3caf50",
      text: "white",
    },
    {
      key: "uncollectible",
      bg: "#af683c",
    },
    {
      key: "void",
      bg: "tomato",
      text: "white",
    },
  ];

  return (
    <>
      {openCardModal && (
        <CardInfoModal
          isOpen={openCardModal}
          onClose={() => setOpenCardModal(false)}
        />
      )}
      <div className="bg-[#fafafa] max-w-[100%] w-full px-7 py-5">
        <div className="flex justify-between items-center">
          <div className="text-2xl font-bold">Manage Your Subscription</div>
        </div>

        <div className="mt-8">
          {plans.length < 1 ? (
            <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
              <h2 className="text-xl font-semibold mb-3">Action required</h2>
              <p className="mb-2">
                Hello {admin.firstname}, your subscription is still active but
                in light of our new payment integration an action is required of
                you to view your subscription
              </p>
              <p className="mb-2">
                Clicking the button below would transfer your subscription to
                our new platform with every detail and you'll only be charged
                when your current subscription elapses.
              </p>
              <p className="mb-2">
                Your current subscription with our previous platform would also
                be cancelled.
              </p>
              {!facility.mx_card_id ? (
                <>
                  <p className="text-red-500 mb-2">
                    Your facility currently has no active credit card info that
                    can be used for the Subscription.
                  </p>
                  <button
                    className="cursor-pointer bg-primary h-[35px] w-[150px] flex justify-center items-center rounded-md text-white hover:opacity-60 transition duration-300 "
                    onClick={() => setOpenCardModal(true)}
                  >
                    Add card
                  </button>
                </>
              ) : (
                <button
                  className="cursor-pointer bg-primary h-[40px] w-[200px] flex justify-center items-center rounded-md text-white hover:opacity-60 transition duration-300  disabled:opacity-60"
                  onClick={handleTransfer}
                  disabled={loading}
                >
                  {loading === "transfer" ? (
                    <Spinner color={"white"} />
                  ) : (
                    "Transfer subscription"
                  )}
                </button>
              )}
            </div>
          ) : (
            <div>
              <div className="flex justify-between items-center">
                <span className="font-medium">All plans</span>
                <button
                  className="w-[40px] h-[40px] rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 duration-300 transition-colors"
                  onClick={() => setOpenCardModal(true)}
                >
                  <Setting2 size={20} />
                </button>
              </div>
              <div className="mt-5 grid gap-5 md:grid-cols-2">
                {plans.map((plan) => {
                  let btnText = "Select plan";
                  if (loading === plan.id) {
                    btnText = <Spinner color={"white"} />;
                  } else if (plan.currentPlan) {
                    btnText = "Cancel Subscription";
                  }
                  return (
                    <div
                      key={plan.id}
                      className={`relative bg-white rounded-lg shadow-sm overflow-hidden border-2 transition-colors ${
                        plan.currentPlan
                          ? "border-primary"
                          : "hover:border-gray-200"
                      }`}
                    >
                      <div className="p-6 h-full flex flex-col justify-between">
                        <div>
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="text-xl font-semibold text-gray-900">
                                {plan.name}
                              </h3>
                              <div className="mt-1 flex items-baseline gap-x-1">
                                <span className="text-2xl font-bold text-gray-900">
                                  ${plan.amount.toLocaleString()}
                                </span>
                                <span className="text-sm text-gray-500">
                                  /month
                                </span>
                              </div>
                            </div>
                            {plan.currentPlan && (
                              <span className="inline-flex items-center rounded-full bg-teal-50 px-2 py-1 text-xs font-medium text-teal-700 ring-1 ring-inset ring-teal-600/20">
                                Current
                              </span>
                            )}
                          </div>

                          <SubscriptionBadge
                            active={plan.active}
                            renewalDate={new Date(
                              plan.renewalDate
                            ).toLocaleDateString()}
                            isFree={plan.name.toLowerCase() === "free"}
                          />

                          <div className="mt-6 space-y-3">
                            {plan.benefits.slice(0, 3).map((feature, index) => (
                              <PlanFeature key={index}>{feature}</PlanFeature>
                            ))}
                          </div>
                        </div>
                        <div className="mt-6 flex justify-between items-center">
                          {plan.benefits.length > 3 && (
                            <button
                              onClick={() => setViewingPlan(plan)}
                              className="text-teal-600 hover:text-teal-700 text-sm font-medium"
                            >
                              See more
                            </button>
                          )}
                          {plan.name.toLowerCase() !== "free" && (
                            <button
                              className={`flex justify-center items-center text-center  text-white text-xs ${
                                plan.currentPlan ? "bg-[tomato]" : "bg-primary"
                              } w-full max-w-[150px] h-[35px] rounded-md`}
                              onClick={() => handlePlanChange(plan)}
                              disabled={Boolean(loading)}
                            >
                              {btnText}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {history.length >= 1 && (
                <div className="mt-10">
                  <span className="font-medium">Billing history</span>
                  <div className="border max-h-[58vh] bg-white rounded-md overflow-x-scroll scrollbar-hide mt-4">
                    <div className="max-h-[80vh] overflow-y-scroll scrollbar-hide">
                      <table className="w-full relative whitespace-nowrap">
                        <thead>
                          <tr className="uppercase h-14 font-bold bg-[#f1f1f1] text-gray-700 text-[12px]">
                            <td className="px-6 py-4">#</td>
                            <td className="px-6 py-4">Status</td>
                            <td className="px-6 py-4">Amount</td>
                            <td className="px-6 py-4">Date</td>
                            {/* <td className="px-6 py-4"></td>
                            <td className="px-6 py-4"></td> */}
                          </tr>
                        </thead>

                        <tbody>
                          {history.map((history, index) => {
                            const statusBg = statusBgs.find(
                              (stat) => history.status === stat.key
                            );
                            return (
                              <tr
                                key={index}
                                className="h-14 text-[14px] border-b"
                              >
                                <td className="px-6 py-4">{index + 1}</td>
                                <td className={`px-6 py-4 `}>
                                  <span
                                    style={{
                                      background: statusBg?.bg || "#444",
                                      color: statusBg?.text
                                        ? statusBg?.text
                                        : "black",
                                    }}
                                    className={`inline-block rounded py-1 text-sm px-3.5 capitalize`}
                                  >
                                    {history.status}
                                  </span>
                                </td>
                                <td className="px-6 py-2">
                                  ${+history.amount.toLocaleString()}
                                </td>
                                <td className="px-6 py-2">
                                  {new Date(
                                    history.settledDate
                                  ).toLocaleString()}
                                </td>
                                {/* <td
                                  onClick={() =>
                                    window.open(
                                      history.hosted_invoice_url,
                                      "_blank"
                                    )
                                  }
                                  className="px-6 py-4 underline text-primary cursor-pointer"
                                >
                                  View history
                                </td>
                                <td
                                  className="px-6 py-4 hover:text-primary cursor-pointer"
                                  onClick={() => {
                                    window.open(history.invoice_pdf, "_blank");
                                  }}
                                >
                                  <DocumentDownload />
                                </td> */}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {openCancelModal && (
          <CancelSubscriptionComp
            setOpenCancelModal={setOpenCancelModal}
            admin={admin}
          />
        )}
        {openFreePlanModal && (
          <CancelSubscriptionComp
            setOpenCancelModal={setOpenFreePlanModal}
            admin={admin}
            type="free"
          />
        )}
        <Drawer
          isOpen={!!viewingPlan}
          onClose={() => setViewingPlan(null)}
          plan={viewingPlan}
          loading={loading}
          handlePlanChange={handlePlanChange}
        />
        {viewingPlan && (
          <div
            className="fixed inset-0 bg-[rgba(0,0,0,.2)] transition-opacity z-40"
            onClick={() => setViewingPlan(null)}
          ></div>
        )}
      </div>
    </>
  );
};

const Drawer = ({ isOpen, onClose, plan, loading, handlePlanChange }) => {
  if (!isOpen) return null;

  let btnText = "Select plan";
  if (loading === plan.id) {
    btnText = <Spinner color={"white"} />;
  } else if (plan.currentPlan) {
    btnText = "Cancel Subscription";
  }
  return (
    <div className="fixed top-0 bottom-0 h-[100vh] right-0 w-[450px] z-50">
      {/* <div className="absolute inset-0 overflow-hidden"> */}
      <section className="pl-10 w-full flex">
        <div className="w-full">
          <div className="h-screen flex flex-col bg-white shadow-xl overflow-y-scroll">
            <div className="p-6">
              <div className="flex items-start justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  {plan.name} Plan
                </h2>
                <button
                  onClick={onClose}
                  className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <CloseCircle className="h-6 w-6" />
                </button>
              </div>
              <div className="mt-4">
                <div className="flex items-baseline gap-x-1">
                  <span className="text-3xl font-bold text-gray-900">
                    ${plan.amount.toLocaleString()}
                  </span>
                  <span className="text-sm text-gray-500">/month</span>
                </div>
                <SubscriptionBadge
                  isFree={plan.name.toLowerCase() === "free"}
                  active={plan.active}
                  renewalDate={new Date(plan.renewalDate).toLocaleDateString()}
                />
              </div>
              <div className="mt-6 space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Features
                  </h3>
                  <ul className="mt-4 space-y-3">
                    {plan.benefits.map((feature, index) => (
                      <li key={index}>
                        <PlanFeature>{feature}</PlanFeature>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8">
                <button
                  className={`flex justify-center items-center text-center  text-white text-xs ${
                    plan.currentPlan ? "bg-[tomato]" : "bg-primary"
                  } w-full h-[35px] rounded-md`}
                  onClick={() => handlePlanChange(plan)}
                  disabled={Boolean(loading)}
                >
                  {btnText}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* </div> */}
    </div>
  );
};

const PlanFeature = ({ children }) => (
  <div className="flex items-center gap-2 text-gray-600">
    <img src="/check.svg" height={20} width={20} />
    <span>{children}</span>
  </div>
);

const SubscriptionBadge = ({ active, renewalDate, isFree }) => {
  if (!active) return null;

  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="flex items-center gap-1.5">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
        </span>
        Active
      </span>
      {!isFree && (
        <>
          <span className="text-gray-400">•</span>
          <span className="text-gray-600">Renews {renewalDate}</span>
        </>
      )}
    </div>
  );
};

const CancelSubscriptionComp = ({ setOpenCancelModal, admin, type }) => {
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const token = getToken();

  useEffect(() => {
    let subscribed = true;
    (async () => {
      try {
        const res = await instance.get("/doctor/facility/doctors", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (subscribed) {
          const docs = res.data.filter(
            (doc) => doc.id !== admin.id && doc.account_status !== "deactivated"
          );
          setDoctors(docs);
          setLoading(false);
        }
      } catch (err) {
        toast.error("An error occurred. Please try reloading");
        subscribed && setOpenCancelModal(false);
      }
    })();

    return () => {
      subscribed = false;
    };
  }, []);

  const handleCancelSubscription = async () => {
    try {
      setIsLoading(true);
      const res = await instance.get("/doctor/billing/cancel-subscription", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Subscription canceled");
      window.location.reload();
      // setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setErrorMessage(error?.response?.data?.message);
    }
  };

  return (
    <>
      <div className="fixed min-w-[400px] w-[768px] top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 z-40 bg-white overflow-y-scroll scrollbar-hide rounded-md">
        <div className="sticky left-0 right-0 top-0">
          <div className="bg-[#f1f1f1] px-5 py-4 flex justify-between">
            <div className="text-[18px] font-bold select-none">
              Confirm action
            </div>
            <button onClick={() => setOpenCancelModal(false)}>
              <CloseSquare variant="Bold" />
            </button>
          </div>
        </div>

        <div className="mt-2 px-5 mb-5">
          {type === "free" ? (
            <>
              <span className="block font-medium pb-1">
                Are you sure you want to switch to a free plan?
              </span>
              <span className="opacity-60 pb-5 block">
                Switching to a free plan would cancel your current subscription.
              </span>
            </>
          ) : (
            <span className="block font-medium pb-5">
              Are you sure you want to cancel this subscription?
            </span>
          )}
          {doctors.length >= 2 && (
            <>
              <span className="text-sm">
                You have <strong>{doctors.length}</strong> other active staff
                accounts that would be deactivated on cancellation. Are you sure
                you want to proceed?
              </span>
            </>
          )}
        </div>
        <div className="mt-4 px-5 mb-5">
          <div className="text-[14px] font-semibold py-1 text-red-500">
            {errorMessage}
          </div>
          <PrimaryAddButton
            title={
              type === "free" ? "Switch to free plan" : "Cancel Subscribtion"
            }
            isLoading={isLoading}
            disabled={isLoading}
            color="white"
            bg={type === "free" ? "" : "bg-[tomato]"}
            onClick={handleCancelSubscription}
          />
        </div>
      </div>
      <div
        className="fixed z-20 inset-0 bg-[rgba(0,0,0,.2)]"
        onClick={() => setOpenCancelModal(false)}
      />
    </>
  );
};
